import { R as XIcon, ac as CheckIcon, sr as PencilIcon, ws as CopyIcon } from "./icons-BxyjaHfi.mjs";
import { $t as cx, Kt as Button$1, L as NotificationWrapper, Nt as Breadcrumbs$1, Ut as Dropdown, Zt as popoverAnimationClasses, in as Badge, k as ProgressBar$1, n as Transition, on as Logo, r as ToggleButton$1 } from "./negroni-Cb3a5kWy.mjs";
import { Children, createContext, forwardRef, isValidElement, useContext, useEffect, useMemo, useRef, useState } from "react";
import { tv } from "tailwind-variants";
import { Fragment as Fragment$1, jsx, jsxs } from "react/jsx-runtime";
import { Button, Dialog, DialogTrigger, Heading, Popover } from "react-aria-components";
//#region src/utils/getComponentByType.ts
/**
* Find children components by component type
* @example
* const componentReactNodes = getComponentByType(children, Component);
*/
const getComponentByType = (children, componentType) => {
	if (children == null) return [];
	const components = [];
	Children.forEach(children, (child) => {
		if (isValidElement(child) && child.type === componentType) components.push(child);
	});
	return components;
};
//#endregion
//#region src/utils/isTouchDevice.ts
const isTouchDevice = () => typeof window === "undefined" ? false : "ontouchstart" in window || navigator.maxTouchPoints > 0;
//#endregion
//#region src/application/contexts/GlobalActionProvider.tsx
const GlobalActionContext = createContext({});
/**
* Provides content that should be available on all pages, e.g. the main chat
* button, as well as a mechanism to selectively disable it for particular use cases.
* ComposerNav is the primary consumer inside Negroni.
*/
const GlobalActionProvider = ({ children, globalAction }) => {
	const [showGlobalAction, setShowGlobalAction] = useState(true);
	const values = useMemo(() => ({
		globalAction: showGlobalAction ? globalAction : null,
		setShowGlobalAction
	}), [
		globalAction,
		showGlobalAction,
		setShowGlobalAction
	]);
	return /* @__PURE__ */ jsx(GlobalActionContext.Provider, {
		value: values,
		children
	});
};
function useGlobalActionContext() {
	const context = useContext(GlobalActionContext);
	if (!context) throw new Error("useGlobalActionContext needs a provider!");
	return context;
}
//#endregion
//#region src/application/ComposerNav/ComposerNav.tsx
const ComposerNavPropsContext = createContext({});
const composerNavStyles = tv({
	slots: {
		container: "relative flex flex-col items-start justify-between gap-2 px-4 py-3 lg:flex-row lg:items-center lg:gap-0",
		titleAndBadgeContainer: "flex flex-col gap-2 lg:flex-row lg:gap-6",
		titleContainer: "flex min-w-0 items-center gap-1",
		closeContainer: "flex items-center justify-center lg:h-8 lg:w-8",
		badgeContainer: "flex items-center gap-2",
		actionsContainer: "flex flex-wrap items-center gap-1",
		secondaryAction: "",
		saveTagContainer: "flex items-center gap-2 px-2 text-body-1 text-neutral-600",
		progressBar: "order-last w-full items-center justify-center py-4 md:order-none md:flex-1 md:px-8 md:py-0",
		titleSpan: "text-body-1 font-semibold text-neutral-800"
	},
	variants: {
		hasStickyHeader: { true: { container: "sticky top-0 z-10 bg-neutral-50" } },
		hideTextForMobile: { true: { secondaryAction: "sr-only md:not-sr-only" } },
		showProgressBar: { true: {
			container: "flex-wrap",
			titleAndBadgeContainer: "w-10/12 md:w-auto",
			titleContainer: "flex-1 justify-between md:justify-normal",
			titleSpan: "ml-auto w-full text-center text-body-1 font-semibold text-neutral-800 md:ml-0"
		} }
	}
});
function SaveTag({ text, icon: Icon, onClick }) {
	const { saveTagContainer } = composerNavStyles();
	const handleKeyDown = (e) => {
		if (e.key === "Enter") onClick?.();
	};
	return /* @__PURE__ */ jsx("div", {
		onClick,
		onKeyDown: handleKeyDown,
		role: "button",
		tabIndex: 0,
		children: /* @__PURE__ */ jsxs("div", {
			className: saveTagContainer(),
			children: [Icon && /* @__PURE__ */ jsx(Icon, { size: "sm" }), text]
		})
	});
}
const SecondaryAction = forwardRef(({ "aria-label": ariaLabel, text, icon, onClick, hideTextForMobile, isDisabled = false, isActive, onChange, className, notificationsCount, notificationsLabel }, ref) => {
	const { secondaryAction } = composerNavStyles({ hideTextForMobile });
	const button = onChange ? /* @__PURE__ */ jsx(ToggleButton$1, {
		ref,
		"aria-label": ariaLabel,
		isSelected: isActive,
		leftIcon: icon,
		onChange,
		isDisabled,
		children: /* @__PURE__ */ jsx("span", {
			className: secondaryAction(),
			children: text
		})
	}) : /* @__PURE__ */ jsx(Button$1, {
		ref,
		"aria-label": ariaLabel,
		onPress: onClick,
		icon,
		size: "sm",
		color: "neutral",
		isDisabled,
		className,
		children: /* @__PURE__ */ jsx("span", {
			className: secondaryAction(),
			children: text
		})
	});
	return notificationsLabel ? /* @__PURE__ */ jsx(NotificationWrapper, {
		label: notificationsLabel,
		notifications: notificationsCount,
		children: button
	}) : button;
});
SecondaryAction.displayName = "ComposerNav.SecondaryAction";
const PrimaryAction = forwardRef(({ "aria-label": ariaLabel, text, onClick, icon, ...buttonProps }, ref) => {
	return /* @__PURE__ */ jsx(Button$1, {
		...buttonProps,
		"aria-label": ariaLabel,
		onPress: onClick,
		icon,
		size: "sm",
		ref,
		children: text
	});
});
PrimaryAction.displayName = "ComposerNav.PrimaryAction";
function Actions({ children, className }) {
	return /* @__PURE__ */ jsx("div", {
		className: cx("flex flex-wrap gap-1", className),
		children
	});
}
function NavDropdown({ children, onAction }) {
	return /* @__PURE__ */ jsx(Dropdown, {
		type: "icon",
		size: "sm",
		onAction,
		children: getComponentByType(children, Dropdown.Item)
	});
}
NavDropdown.Item = Dropdown.Item;
/**
* Header component, primarily intended for use in [FullPageTakeover](https://storybook.my-account-rex.dev.aws-01.legalzoom.com/?path=/docs/application-fullpagetakeover--docs) (`FullPageTakeover.Header` = `ComposerNav`).
* It is a highly opinionated, slotted component that supports the following sub-components
* (see specific examples for usage):
* ```
<ComposerNav.Breadcrumbs />
<ComposerNav.SaveTag />
<ComposerNav.PrimaryAction />
<ComposerNav.SecondaryAction />
<ComposerNav.Dropdown>
<ComposerNav.Dropdown.Item/>
<ComposerNav.Actions />
* ```
*/
function ComposerNav(props) {
	const contextProps = useContext(ComposerNavPropsContext);
	const { title, showLogo = false, logoSize = "sm", progress, showProgressBar, subscription, status, statusColor = "neutral", statusIcon, statusIconProps, children, hasStickyHeader, icon = XIcon, isDisabled = false, onClose } = {
		...props,
		...contextProps
	};
	const { container, closeContainer, actionsContainer, badgeContainer, titleSpan, titleAndBadgeContainer, titleContainer, progressBar } = composerNavStyles({
		hasStickyHeader,
		showProgressBar
	});
	const [breadcrumbs] = getComponentByType(children, Breadcrumbs$1);
	const [saveItem] = getComponentByType(children, SaveTag);
	const secondaryActions = getComponentByType(children, SecondaryAction);
	const [primaryAction] = getComponentByType(children, PrimaryAction);
	const [actions] = getComponentByType(children, Actions);
	const [dropdown] = getComponentByType(children, NavDropdown);
	const { globalAction } = useGlobalActionContext();
	useEffect(() => {
		const closeOnEscape = (e) => {
			if (e.key === "Escape" && onClose) onClose();
		};
		document.addEventListener("keydown", closeOnEscape);
		return () => {
			document.removeEventListener("keydown", closeOnEscape);
		};
	}, [onClose]);
	return /* @__PURE__ */ jsxs("div", {
		className: container(),
		children: [
			/* @__PURE__ */ jsxs("div", {
				className: titleAndBadgeContainer(),
				children: [/* @__PURE__ */ jsxs("div", {
					className: titleContainer(),
					children: [
						onClose && /* @__PURE__ */ jsx("span", {
							className: closeContainer(),
							children: /* @__PURE__ */ jsx(Button$1, {
								"aria-label": "Close",
								icon,
								onPress: onClose,
								size: "sm",
								variant: "ghost",
								color: "neutral",
								isDisabled
							})
						}),
						showLogo && /* @__PURE__ */ jsx("a", {
							href: "/",
							children: /* @__PURE__ */ jsx(Logo, {
								variant: "mark",
								size: logoSize
							})
						}),
						title ? /* @__PURE__ */ jsx("span", {
							className: titleSpan(),
							children: title
						}) : breadcrumbs
					]
				}), (subscription || status || saveItem) && /* @__PURE__ */ jsxs("div", {
					className: badgeContainer(),
					children: [
						subscription && /* @__PURE__ */ jsx(Badge, {
							label: `[${subscription}]`,
							size: "lg"
						}),
						status && /* @__PURE__ */ jsx(Badge, {
							label: status,
							icon: statusIcon,
							iconProps: statusIconProps,
							size: "lg",
							color: statusColor
						}),
						saveItem
					]
				})]
			}),
			showProgressBar && /* @__PURE__ */ jsx("div", {
				className: progressBar(),
				children: /* @__PURE__ */ jsx(ProgressBar$1, {
					"aria-label": "progress-bar",
					showValueText: false,
					value: progress,
					color: "accent",
					size: "lg"
				})
			}),
			(secondaryActions.length || primaryAction || actions || dropdown || globalAction) && /* @__PURE__ */ jsxs("div", {
				className: actionsContainer(),
				children: [
					/* @__PURE__ */ jsx("div", {
						className: "absolute right-4 top-3 lg:static",
						children: globalAction
					}),
					actions,
					secondaryActions,
					primaryAction,
					dropdown
				]
			})
		]
	});
}
ComposerNav.Breadcrumbs = Breadcrumbs$1;
ComposerNav.SaveTag = SaveTag;
/** @deprecated Please use generic `Action` container instead.*/
ComposerNav.SecondaryAction = SecondaryAction;
/** @deprecated Please use generic `Action` container instead.*/
ComposerNav.PrimaryAction = PrimaryAction;
ComposerNav.Actions = Actions;
/** @deprecated Please use generic `Action` container instead.*/
ComposerNav.Dropdown = NavDropdown;
//#endregion
//#region src/application/Shell/ShellContext.ts
const ShellContext = createContext({});
const useShellContext = () => useContext(ShellContext);
//#endregion
//#region src/negroni/utils/createPageTakeoverOpenEvent.ts
const eventType = "pagetakeover_open";
function createPageTakeoverOpenEvent(detail) {
	return new CustomEvent(eventType, {
		detail,
		bubbles: true,
		cancelable: false
	});
}
//#endregion
//#region src/application/FullPageTakeOver/Fpt.common.tsx
const emitPageTakeOverEvent = (ref) => {
	if (!ref.current) return;
	const pageTakeOverEvent = createPageTakeoverOpenEvent({
		id: ref.current.id,
		title: ref.current.querySelector(".text-title-3")?.textContent?.toString()
	});
	ref.current.dispatchEvent(pageTakeOverEvent);
};
const Content = ({ className, children, ...restProps }) => /* @__PURE__ */ jsx("div", {
	className: "grow overflow-auto",
	children: /* @__PURE__ */ jsx("div", {
		className: cx("mx-auto lg:max-w-7xl", className),
		...restProps,
		children
	})
});
//#endregion
//#region src/application/FullPageTakeOver/FptLayout.tsx
/**
* Simple layout for use with FPTs. Use this component when not overlaying content,
* i.e. as the root layout of a route.
*
* Subcomponents
* ```
<FptLayout.Header /> // ComposerNav
<FptLayout.Content />
* ```
*/
const FptLayout = ({ className, children, onClose, ...rest }) => {
	const { isLoading, customLoader } = useShellContext();
	const contentRef = useRef(null);
	useEffect(() => {
		if (contentRef.current !== null) emitPageTakeOverEvent(contentRef);
	}, []);
	return /* @__PURE__ */ jsx(ComposerNavPropsContext.Provider, {
		value: { onClose },
		children: /* @__PURE__ */ jsxs("div", {
			"data-nds-takeover": true,
			className: cx("flex h-dvh w-dvw flex-col bg-neutral-50 text-body-1 text-neutral-800", className),
			...rest,
			ref: contentRef,
			children: [isLoading && customLoader && /* @__PURE__ */ jsx("div", {
				className: "absolute inset-0 z-30 flex items-center justify-center",
				children: customLoader
			}), children]
		})
	});
};
FptLayout.Header = ComposerNav;
FptLayout.Content = Content;
//#endregion
//#region src/application/FullPageTakeOver/FullPageTakeOver.tsx
const fptModalStyles = tv({
	base: "fixed inset-0 z-30 flex h-dvh w-dvw",
	variants: { animation: {
		none: "",
		fade: "transition duration-200 data-[closed]:opacity-0",
		slide: "transition duration-200 motion-safe:data-[closed]:translate-y-full motion-reduce:data-[closed]:opacity-0"
	} }
});
/**
* Full-page takeover (FPT) which overlays content on the page. If your FPT is at the
* root of a route and not overlaying content, use the underlying `FptLayout` instead.
*
* Subcomponents are available from  both FptModal and FptLayout
* (e.g. `FptModal.Header` === `FptLayout.Header`):
* ```
<Header>
<Content>
* ```
* See [ComposerNav documentation](https://storybook.my-account-rex.dev.aws-01.legalzoom.com/?path=/docs/application-composernav--docs) for Header subcomponents and usage.
*/
const FullPageTakeOver = ({ animation = "slide", disableAnimation = false, children, className, isOpen, ...rest }) => {
	const styles = fptModalStyles({
		animation: disableAnimation ? "none" : animation || "none",
		className
	});
	useEffect(() => {
		isOpen ? document.body.classList.add("overflow-hidden") : document.body.classList.remove("overflow-hidden");
		return () => {
			document.body.classList.remove("overflow-hidden");
		};
	}, [isOpen]);
	return /* @__PURE__ */ jsx(Transition, {
		show: isOpen,
		appear: true,
		children: /* @__PURE__ */ jsx("div", {
			className: styles,
			children: /* @__PURE__ */ jsx("div", {
				className: "h-full w-full outline-none",
				children: /* @__PURE__ */ jsx(FptLayout, {
					...rest,
					children
				})
			})
		})
	});
};
FullPageTakeOver.Header = ComposerNav;
FullPageTakeOver.Content = Content;
//#endregion
//#region src/application/InfoChip/InfoChip.tsx
const infoChipStyles = tv({
	slots: {
		button: "group relative flex h-[68px] w-full grow flex-col justify-between rounded-lg border p-3 text-left outline-none transition ease-in-out disabled:pointer-events-none disabled:border data-[hovered]:shadow-level-3 data-[hovered]:shadow-neutral-50",
		icon: "absolute right-3 top-3 text-black group-disabled:text-neutral-300"
	},
	variants: {
		intent: {
			info: { button: "border-neutral-50 bg-neutral-25 data-[focus-visible]:border-neutral-200 data-[hovered]:border-neutral-200 data-[focus-visible]:bg-neutral-100 data-[hovered]:bg-neutral-100" },
			success: { button: "border-success-500 bg-success-25 text-success-800 hover:bg-success-100" },
			error: { button: "border-error-500 bg-error-25 text-error-800 hover:bg-error-100" }
		},
		isClipboardDisabled: { true: {
			button: "pointer-events-none",
			icon: "invisible"
		} },
		isPlaceholder: { true: { button: "pointer-events-none" } },
		alwaysShowIcon: {
			true: { icon: "" },
			false: { icon: "hidden group-data-[focus-visible]:block group-data-[hovered]:block" }
		}
	},
	defaultVariants: { intent: "info" }
});
let lastOpenedTime = 0;
const throttleInterval = 500;
const throttle = (callback) => {
	const now = Date.now();
	if (now - lastOpenedTime > throttleInterval) {
		lastOpenedTime = now;
		callback();
	}
};
function InfoChip(props) {
	const { title, label, children, onClick, isDisabled, intent, isClipboardDisabled = false, isEditable, editText } = props;
	const [open, setOpen] = useState(false);
	const [timeoutHandle, setTimeoutHandle] = useState(null);
	const { button, icon } = infoChipStyles({
		intent,
		isClipboardDisabled,
		isPlaceholder: Boolean(!children) && Boolean(!label) && Boolean(!isEditable)
	});
	const isTouch = isTouchDevice();
	useEffect(() => {
		return () => {
			timeoutHandle && clearTimeout(timeoutHandle);
		};
	}, [timeoutHandle]);
	const onClickHandler = () => {
		const copyToClipboard = async () => {
			if (navigator.clipboard && label) try {
				await navigator.clipboard.writeText(label);
				throttle(() => {
					setOpen(true);
					setTimeoutHandle(setTimeout(() => {
						setOpen(false);
					}, 2e3));
				});
			} catch (e) {
				if (e.name !== "NotAllowedError") throw e;
			}
			else if (!isEditable) console.warn("Clipboard API not available or label is undefined");
		};
		copyToClipboard();
		onClick?.();
	};
	const copyIcon = () => /* @__PURE__ */ jsx("div", {
		className: icon({ alwaysShowIcon: open || isTouch }),
		children: open ? /* @__PURE__ */ jsx(CheckIcon, { size: "sm" }) : /* @__PURE__ */ jsx(CopyIcon, { size: "sm" })
	});
	const renderContent = () => {
		if (label && !children) return /* @__PURE__ */ jsxs(Fragment$1, { children: [copyIcon(), /* @__PURE__ */ jsx("span", {
			className: "text-md line-clamp-1 h-6 w-full text-body-1 font-semibold group-disabled:text-neutral-300",
			children: label
		})] });
		if (children) return /* @__PURE__ */ jsxs(Fragment$1, { children: [copyIcon(), children] });
		if (!!isEditable && editText) return /* @__PURE__ */ jsxs("span", {
			"data-testid": "placeholder",
			className: "mt-1 inline-flex h-4 w-full justify-between rounded text-body-1 font-semibold text-neutral-300",
			children: [editText, /* @__PURE__ */ jsx(PencilIcon, { size: "sm" })]
		});
		return /* @__PURE__ */ jsxs("span", {
			"data-testid": "placeholder",
			className: "h-4 text-body-1 font-semibold text-neutral-300",
			children: ["– ", /* @__PURE__ */ jsx("span", {
				className: "sr-only",
				children: "None"
			})]
		});
	};
	return /* @__PURE__ */ jsxs(DialogTrigger, {
		isOpen: open,
		children: [/* @__PURE__ */ jsx(Button, {
			className: button(),
			onPress: onClickHandler,
			isDisabled,
			children: /* @__PURE__ */ jsxs("div", {
				className: "flex w-full flex-col gap-1",
				children: [/* @__PURE__ */ jsx("span", {
					className: "line-clamp-1 text-label uppercase text-neutral-600 group-disabled:text-neutral-300",
					children: title
				}), renderContent()]
			})
		}), /* @__PURE__ */ jsx(Popover, {
			isNonModal: true,
			offset: 4,
			className: popoverAnimationClasses,
			children: /* @__PURE__ */ jsx(Dialog, {
				className: "rounded-lg border border-neutral-100 bg-white shadow-level-3 outline-none",
				children: /* @__PURE__ */ jsx(Heading, {
					className: "p-1 px-2 text-xs",
					slot: "title",
					children: "Copied!"
				})
			})
		})]
	});
}
//#endregion
export { ShellContext as a, ComposerNavPropsContext as c, isTouchDevice as d, getComponentByType as f, FptLayout as i, GlobalActionProvider as l, infoChipStyles as n, useShellContext as o, FullPageTakeOver as r, ComposerNav as s, InfoChip as t, useGlobalActionContext as u };

//# sourceMappingURL=InfoChip-CmK9ustE.mjs.map